import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { fromEvent } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-mention-list',
  template: `
    <div class="mention-list-container" #container>
      <div class="mention-items" #mentionItems>
        <div
          *ngFor="let item of filteredItems"
          class="mention-item"
          (click)="selectItem(item)"
          [class.active]="item === activeItem"
          #mentionItem
        >
          {{ item[labelKey] }}
        </div>
      </div>
    </div>
  `,
  styles: [`
    .mention-list-container {
      background: white;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      min-width: 200px;
      max-width: 300px;
    }
    .mention-items {
      max-height: 200px;
      overflow-y: auto;
      padding: 4px 0;
    }
    .mention-item {
      padding: 8px 12px;
      cursor: pointer;
      transition: all 0.2s;
      font-size: 13px;
    }
    .mention-item:hover, .mention-item.active {
      background-color: #3367d6;
      color: white;
    }
  `]
})
export class MentionListComponent {
  @Input() items: any[] = [];
  @Input() labelKey: string = 'value';
  @Input() triggerChar: string = '{';
  @Output() itemSelected = new EventEmitter<any>();
  @ViewChild('container') container: ElementRef;
  @ViewChild('mentionItems') mentionItemsContainer: ElementRef;

  filteredItems: any[] = [];
  activeItem: any = null;
  private overlayRef: OverlayRef;

  constructor(private overlay: Overlay) {}

  ngOnInit() {
    this.filteredItems = [...this.items];
  }

  selectItem(item: any) {
    this.itemSelected.emit(item);
    this.close();
  }

  show(target: ElementRef, position: { x: number; y: number }) {
    const positions: ConnectedPosition[] = [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 8
      }
    ];

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(target)
        .withPositions(positions)
        .withDefaultOffsetY(8),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: '250px',
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop'
    });

    const componentPortal = new ComponentPortal(MentionListComponent);
    const componentRef = this.overlayRef.attach(componentPortal);
    
    // Handle backdrop click
    this.overlayRef.backdropClick().subscribe(() => this.close());

    // Handle escape key
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(
        filter(event => event.key === 'Escape'),
        takeUntil(this.overlayRef.detachments())
      )
      .subscribe(() => this.close());
  }

  close() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
    }
  }

  moveSelection(direction: 'up' | 'down') {
    const currentIndex = this.filteredItems.indexOf(this.activeItem);
    if (direction === 'up') {
      this.activeItem = currentIndex > 0 
        ? this.filteredItems[currentIndex - 1]
        : this.filteredItems[this.filteredItems.length - 1];
    } else {
      this.activeItem = currentIndex < this.filteredItems.length - 1
        ? this.filteredItems[currentIndex + 1]
        : this.filteredItems[0];
    }

    setTimeout(() => {
      const container = this.mentionItemsContainer.nativeElement;
      const activeElements = container.getElementsByClassName('active');
      if (activeElements.length) {
        const activeElement = activeElements[0];
        const containerTop = container.scrollTop;
        const containerBottom = containerTop + container.clientHeight;
        const elementTop = activeElement.offsetTop;
        const elementBottom = elementTop + activeElement.offsetHeight;

        if (elementTop < containerTop) {
          container.scrollTop = elementTop;
        } else if (elementBottom > containerBottom) {
          container.scrollTop = elementBottom - container.clientHeight;
        }
      }
    });
  }
} 